import { inject, Service } from '@angular/core';
import {environment} from '../../environments/environment';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {GoogleService} from './google-service';
import {Observable} from 'rxjs';
import {SelectionProductLocation} from '../model/selection-product-location';
import {SelectionSystemType} from '../model/selection-system-type';
import {SelectionProductFamily} from '../model/selection-product-family';
import {FilterType} from '../model/filter-type';
import {FluidType} from '../model/fluid-type';
import {UnitSelectionCriteria} from '../model/unit-selection-criteria';
import {MotorType} from '../model/motor-type';
import {RatingsModel} from '../model/ratings-model';
import {SelectionModelSizeCharacteristic} from '../model/selection-model-size-characteristic';
import {SelectionItem} from '../selections/selection-item';
import {CandidateSearchResponse} from '../model/candidate-search-response';
import {ConditionValueLimit} from '../model/condition-value-limit';

@Service()
export class UnitSelectionService {
  private readonly http = inject(HttpClient);
  private readonly googleService = inject(GoogleService);


  private urlBase = environment.serviceUrl + 'unitSelection/';

  getProductLocations(): Observable<SelectionProductLocation[]> {
    const url = this.urlBase + 'locations';
    return this.http.get<SelectionProductLocation[]>(url, {headers: this.getRequestHeaders()});
  }

  private getRequestHeaders(): HttpHeaders {
    return new HttpHeaders().set(GoogleService.TokenHeader, this.googleService.getAccessToken());
  }

  getProductLocationFamilies(locationId: number): Observable<SelectionProductFamily[]> {
    const url = this.urlBase + 'families/' + locationId;
    return this.http.get<SelectionProductFamily[]>(url, {headers: this.getRequestHeaders()});
  }

  getProductFamilySystemTypes(familyId: number): Observable<SelectionSystemType[]> {
    const url = this.urlBase + 'systemTypes/' + familyId;
    return this.http.get<SelectionSystemType[]>(url, {headers: this.getRequestHeaders()});
  }

  getMotorTypes(locationId: number, familyId: number, voltage: string): Observable<MotorType[]> {
    const url = this.urlBase + 'motorTypes/' + locationId + '/' + familyId + '/' + voltage;
    return this.http.get<MotorType[]>(url, {headers: this.getRequestHeaders()});
  }

  getEngineMotorTypes(engineKey: string): Observable<MotorType[]> {
    const url = this.urlBase + 'engineMotorTypes/' + encodeURIComponent(engineKey);
    return this.http.get<MotorType[]>(url, {headers: this.getRequestHeaders()});
  }

  getMotorVoltages(locationId: number, familyId: number): Observable<SelectionItem[]> {
    const url = this.urlBase + 'motorVoltages/' + locationId + '/' + familyId;
    return this.http.get<SelectionItem[]>(url, {headers: this.getRequestHeaders()});
  }

  getEngineMotorVoltages(engineKey: string): Observable<SelectionItem[]> {
    const url = this.urlBase + 'engineMotorVoltages/' + encodeURIComponent(engineKey);
    return this.http.get<SelectionItem[]>(url, {headers: this.getRequestHeaders()});
  }

  getLocationFamilyFilterTypes(locationId: number, familyId: number): Observable<FilterType[]> {
    const url = this.urlBase + 'filterTypes/' + locationId + '/' + familyId;
    return this.http.get<FilterType[]>(url, {headers: this.getRequestHeaders()});
  }

  getFluidTypes(): Observable<FluidType[]> {
    const url = this.urlBase + 'fluidTypes';
    return this.http.get<FluidType[]>(url, {headers: this.getRequestHeaders()});
  }

  getSelectionCandidates(criteria: UnitSelectionCriteria): Observable<CandidateSearchResponse> {
    const url = this.urlBase + 'getCandidates';
    return this.http.post<CandidateSearchResponse>(url, JSON.stringify(criteria), {headers: this.getPostHeaders()});
  }

  private getPostHeaders(): HttpHeaders {
    return this.getRequestHeaders().set('Content-Type', 'application/json');
  }

  getRatingsModels(): Observable<RatingsModel[]> {
    const url = this.urlBase + 'allModels';
    return this.http.get<RatingsModel[]>(url, {headers: this.getRequestHeaders()});
  }

  getModelSizeCharacteristics(engineKey: string): Observable<SelectionModelSizeCharacteristic[]> {
    const url = this.urlBase + 'modelSizeCharacteristics/' + encodeURIComponent(engineKey);
    return this.http.get<SelectionModelSizeCharacteristic[]>(url, {headers: this.getRequestHeaders()});
  }

  getHorsepowerDescription(horsepower: number): Observable<string> {
    const url = this.urlBase + 'horsepowerDescription/' + horsepower;
    return this.http.get(url, {headers: this.getRequestHeaders(), responseType: 'text'});
  }

  getProductSystemTypes(productId: string): Observable<SelectionSystemType[]> {
    const url = this.urlBase + 'productSystemTypes/' + productId;
    return this.http.get<SelectionSystemType[]>(url, {headers: this.getRequestHeaders()});
  }

  getConditionValueLimits(): Observable<ConditionValueLimit[]> {
    const url = this.urlBase + 'conditionValueLimits';
    return this.http.get<ConditionValueLimit[]>(url, {headers: this.getRequestHeaders()});
  }

  getSureFlowSystemTypes(): Observable<SelectionSystemType[]> {
    const url = this.urlBase + 'sureFlowSystemTypes';
    return this.http.get<SelectionSystemType[]>(url, {headers: this.getRequestHeaders()});
  }

  getSureFlowModels(): Observable<RatingsModel[]> {
    const url = this.urlBase + 'sureFlowModels';
    return this.http.get<RatingsModel[]>(url, {headers: this.getRequestHeaders()});
  }

  getPrimaryCoilCircuits(engineKey: string): Observable<SelectionItem[]> {
    const url = this.urlBase + 'primaryCoilCircuits/' + encodeURIComponent(engineKey);
    return this.http.get<SelectionItem[]>(url, {headers: this.getRequestHeaders()});
  }

  getSecondaryCoilCircuits(engineKey: string): Observable<SelectionItem[]> {
    const url = this.urlBase + 'secondaryCoilCircuits/' + encodeURIComponent(engineKey);
    return this.http.get<SelectionItem[]>(url, {headers: this.getRequestHeaders()});
  }

}
